import * as constants from './config/constants.js';
import Selectors from './utils/selectors.js';
import ElementClassHandler from './utils/element-class-handler.js';
import FormFieldHandler from './utils/form-field-handler.js';
import ResponseHandler from './utils/response-handler.js';
import RequestHandler from './utils/request-handler.js';
import FormValidator from './form-validator.js';
import LoadingOverlay from './loading-overlay.js';

const REQUIRED_MONTHS = 36;

export default class Address {
    constructor(options = {}) {
        this.multipleAddresses = options.multipleAddresses || false;
        this.durationFields = options.durationFields || false;
        this.addressType = options.addressType || '';
        this.elementIdentifier = options.elementIdentifier || '';
        this.manualFormPrefix = options.manualForm;
        this.manualForm = options.manualForm;
        this.searchForm = options.searchForm;
        this.selectors = new Selectors({
            dataAttrId: 'data-address-id'
        });
        this.formValidator = new FormValidator();
        this.container = document.querySelector(this.selectors.dataAttr(this.elementIdentifier + '_block'));
        this.deleteModal = this.elementIdentifier + '_modal';
        this.errorMessage = this.elementIdentifier + '_error';
        this.addressSearchResults = this.elementIdentifier + '_search_results';
        this.addressSearchMessage = this.elementIdentifier + '_search_message';
        this.manualAddressLink = this.elementIdentifier + '_manual_link';
        this.searchAddressLink = this.elementIdentifier + '_search_link';
        this.myAddressList = this.elementIdentifier + '_list';
        this.searchAddressBlock = this.elementIdentifier + '_search_block';
        this.searchAddressButton = this.elementIdentifier + '_find_address_button';
        this.searchAddressDurationBlock = this.elementIdentifier + '_search_duration_block';
        this.addAddressBlock = this.elementIdentifier + '_add_block';
        this.addAddressButton = this.elementIdentifier + '_add_address_button';
        this.updateAddressButton = this.elementIdentifier + '_update_address_button';
        this.updateAddressBlock = this.elementIdentifier + '_update_block';
        this.manualAddressBlock = this.elementIdentifier + '_manual_block';
        this.manualAddressDurationBlock = this.elementIdentifier + '_manual_duration_block';
        this.cancelUpdateAddress = this.elementIdentifier + '_cancel_update';
        this.manualAddressIndex = this.elementIdentifier + '_index';
        this.collectionBlock = this.elementIdentifier + '_collection_block';
        this.configAttribute = 'data-address-config';
        this.nonStandardPostcodeMessage = 'non_standard_postcode_message';
        this.slotPicker = 'slot_picker';
        this.collectionIdentifier = 'data-address-collection-id';
        this.css = {
            currentAddressAdded: 'current-address-added',
            editingCurrentAddress: 'editing-current-address'
        };
        this.myAddresses = [];
        this.matchingAddresses = [];
        this.responseHandler = new ResponseHandler;
        this.requestHandler = new RequestHandler;
        this.elementClassHandler = new ElementClassHandler;
        this.formFieldHandler = new FormFieldHandler;
        this.nonRequiredFields = [
            this.elementIdentifier + '_search_form_houseNumberName',
            this.elementIdentifier + '_manual_form_property',
            this.elementIdentifier + '_manual_form_locality',
        ];
        this.loadingOverlay = new LoadingOverlay;
        this.requiredHistory = REQUIRED_MONTHS;
        this.addressHeading = 'Delivery Address';
        this.checkbox = this.selectors.nameAttr('form[' + options.enableForm + '][enabled]');
        this.useBillingAddressAsDeliveryAddress = 'useBillingAddressAsDeliveryAddress';
        this.geoPostcodeLookupHostname = null;
    }

    setup() {
        document.addEventListener('DOMContentLoaded', () => {
            this.startLoad();
        });
        this.readConfig();
        this.myAddressesApi();
        this.getManualFormToken();
        this.setupSearchResultsListeners();
        this.setupSearchAddressLinkListener();
        this.setupManualAddressLinkListener();
        this.setupSearchAddressButtonListener();
        this.setupSearchInputListener();
        this.setupAddAddressButtonListener();
        this.setupUpdateAddressButtonListener();
        this.setupCancelAddressButtonListener();
        this.setupFormSubmitListener();
        if (this.multipleAddresses) {
            this.elementClassHandler.hide(document.querySelector(this.selectors.dataAttr(this.collectionBlock)));
            this.setRequiredFields(this.selectors.querySelectorArray('.' + constants.FIELD_CLASS, this.container), this.nonRequiredFields);
        }
        this.setupGeoPostcodeLookupHostname();
        this.setupPostCode();
    }

    startLoad() {
        this.elementClassHandler.add(this.container, constants.SPINNER_ACTIVE);
        this.elementClassHandler.add(this.container, 'hide-inner');
        setTimeout(() => {
            if (document.querySelector('.' + constants.SPINNER_ACTIVE)) {
                this.endLoad();
            }
        }, 10000);
    }

    endLoad() {
        this.elementClassHandler.remove(this.container, constants.SPINNER_ACTIVE);
        this.elementClassHandler.remove(this.container, 'hide-inner');
        window.scrollTo(0, 0);
    }

    showError(error, elem = null) {
        const errorBox = elem ? elem : document.querySelector(this.selectors.dataAttr(this.errorMessage));
        errorBox.innerHTML = error;
        this.elementClassHandler.show(errorBox);
        this.elementClassHandler.alertError(errorBox);
    }

    hideError(elem = null) {
        const errorBox = elem ? elem : document.querySelector(this.selectors.dataAttr(this.errorMessage));
        this.elementClassHandler.removeAlerts(errorBox);
        this.elementClassHandler.hide(errorBox);
    }

    getManualFormToken() {
        this.manualFormToken = document.querySelector(this.selectors.nameAttr(this.manualFormPrefix + '[_token]')).value || '';
    }

    myAddressesApi() {
        this.requestHandler.get(
            constants.CHECKOUTPATH + '/' + this.addressType + '/list', '', this.myAddressesApiCallBack.bind(this)
        );
    }

    postcodeToUpper(selector) {
        let postcodeField = document.querySelector(selector);
        if (postcodeField) {
            postcodeField.value = postcodeField.value.toUpperCase();
        }
    }

    formatCollectionId(address) {
        return address.houseNumber.trim() + ',' + (address.postcode.replace(' ', '').toUpperCase());
    }

    checkAddressUnique(address) {
        let newAddressId = this.formatCollectionId(address),
            existingAddressSelector = this.selectors.attrSelector(this.collectionIdentifier, newAddressId);

        if (!this.multipleAddresses) {
            existingAddressSelector = this.selectors.dataAttr('my_billing_address_0') + ' ' + this.selectors.attrSelector(this.collectionIdentifier, newAddressId);
        }

        let existingAddress = this.selectors.querySelectorArray(existingAddressSelector);

        if (existingAddress.length) {
            if (this.multipleAddresses) {
                this.showError('You have already entered this address');
            } else {
                this.showError('Please select a different address for delivery');
            }
        } else {
            this.hideError();
            return true;
        }
    }

    setRequiredFields(fields, exceptions = []) {
        fields.forEach((el) => {
            if (!(exceptions.indexOf(el.id) > -1) && !el.hasAttribute('required')) {
                el.setAttribute('required', 'required');
            }
        });
    }

    removeRequiredFields(fields = null, exceptions = []) {
        const requiredFields = fields || this.selectors.querySelectorArray('.' + constants.FIELD_CLASS, this.container);
        requiredFields.forEach((el) => {
            if (!(exceptions.indexOf(el.id) > -1) && el.hasAttribute('required')) {
                el.removeAttribute('required');
            }
        });
    }

    toggleRequiredFields(type = 'search') {
        let searchRequiredInputs = this.selectors.querySelectorArray(this.selectors.dataAttr(this.searchForm) + ' .' + constants.FIELD_CLASS, this.container),
            manualRequiredInputs = this.selectors.querySelectorArray(this.selectors.dataAttr(this.manualForm) + ' .' + constants.FIELD_CLASS, this.container),
            enableRequiredInputs = (type == 'search') ? searchRequiredInputs : manualRequiredInputs,
            disableRequiredInputs = (type == 'search') ? manualRequiredInputs : searchRequiredInputs;
        this.setRequiredFields(enableRequiredInputs, this.nonRequiredFields);
        this.removeRequiredFields(disableRequiredInputs);
    }

    setupSearchResultsChangeListener() {
        document.querySelector(this.selectors.dataAttr(this.addressSearchResults) + ' select').addEventListener('change', (e) => {
            if (e.target.value == 'Select Address') {
                return;
            }
            const address = this.matchingAddresses[e.target.value] || [];

            if (address.houseNumber && address.postcode && !this.checkAddressUnique(address)) {
                return;
            }
            if (this.multipleAddresses && address.years == 0 && address.months == 0) {
                this.showError('Please enter a least one month');
                return;
            }
            this.elementClassHandler.hide(document.querySelector(this.selectors.dataAttr(this.addressSearchResults)));
            this.clearAddressSearchMessage();

            this.loadingOverlay.taskStarted();

            this.requestHandler.get(
                this.geoPostcodeLookupHostname + '/address/' + e.target.value,
                null,
                this.fetchAddressApiCallBack.bind(this)
            );
        });
    }

    setupGeoPostcodeLookupHostname() {
        const el = document.querySelector('[data-geo-postcode-lookup-hostname]');
        if (el && el.hasAttribute('data-geo-postcode-lookup-hostname')) {
            this.geoPostcodeLookupHostname = el.getAttribute('data-geo-postcode-lookup-hostname').replace(/\/$/g, '');
        } else {
            this.geoPostcodeLookupHostname = 'https://geo.simplifyprod.co.uk';
        }
    }

    setupCancelSearchButtonListener() {
        const element = document.querySelector(this.selectors.dataAttr(this.addressSearchResults) + ' button') || null;
        if (element) {
            element.addEventListener('click', (e) => {
                e.preventDefault();
                this.clearSearchForm();
                this.elementClassHandler.hide(document.querySelector(this.selectors.dataAttr(this.addressSearchResults)));
                this.clearAddressSearchMessage();
                this.validateMyAddressList();
            });
        }
    }

    setupSearchResultsListeners() {
        this.setupSearchResultsChangeListener();
        this.setupCancelSearchButtonListener();
    }

    setupSearchAddressButtonListener() {
        const element = document.querySelector(this.selectors.dataAttr(this.searchAddressButton) + ' button') || null;
        if (element) {
            element.addEventListener('click', (e) => {
                e.preventDefault();
                this.clearAddressDeleteMessage();
                this.matchingAddresses = [];
                this.postcodeToUpper('#' + this.elementIdentifier + '_search_form_postcode');
                let formData = this.formFieldHandler.getFormValues(this.selectors.dataAttr(this.searchForm)),
                    address = this.formToCollectionId(this.searchForm);
                let checkbox = document.querySelector(this.checkbox);
                if (checkbox == null) {
                    formData[this.useBillingAddressAsDeliveryAddress] = false;
                } else {
                    formData[this.useBillingAddressAsDeliveryAddress] = checkbox.checked === true;
                }

                if (address.houseNumber && address.postcode && !this.checkAddressUnique(address)) {
                    return;
                }
                if (this.multipleAddresses && address.years == 0 && address.months == 0) {
                    this.showError('Please enter a least one month');
                    return;
                }
                this.loadingOverlay.taskStarted();

                const data = {
                    query: address.postcode
                };

                this.requestHandler.get(
                    // NOTE: proxy is needed for the dev version
                    this.geoPostcodeLookupHostname + '/address/search',
                    data,
                    this.getAddressSearchApiCallBack.bind(this)
                );
            });
        }
    }

    setupSearchInputListener() {
        const button = document.querySelector(this.selectors.dataAttr(this.searchAddressButton) + ' button'),
            heading = document.querySelector(this.selectors.dataAttr(this.searchAddressDurationBlock) + ' [data-address-heading]');
        let searchPropertyInputs = this.selectors.querySelectorArray(this.selectors.dataAttr(this.searchAddressBlock) + ' input'),
            searchDurationInputs = this.selectors.querySelectorArray(this.selectors.dataAttr(this.searchAddressDurationBlock) + ' input'),
            searchAddressInputs = searchPropertyInputs.concat(searchDurationInputs),
            self = this;
        if (button && searchAddressInputs) {
            this.checkDisableAddressButton(button, searchAddressInputs);
            searchAddressInputs.forEach((el) => {
                let disableFindAttr = 'data-check-disabled-find';
                if (!el.hasAttribute(disableFindAttr)) {
                    el.addEventListener('input', () => {
                        self.checkDisableAddressButton(button, searchAddressInputs);
                    });
                    el.setAttribute(disableFindAttr, '1');
                }
            });
        }
        if (this.durationFields && searchPropertyInputs && searchDurationInputs.length > 1) {
            let animationTriggerAttr = 'data-check-trigger-animation';
            searchPropertyInputs.forEach((el) => {
                if (!el.hasAttribute(animationTriggerAttr)) {
                    el.addEventListener('input', () => {
                        if (searchPropertyInputs[0].validity.valid && searchPropertyInputs[1].validity.valid && searchPropertyInputs[1].value && !searchDurationInputs[0].value && !searchDurationInputs[1].value) {
                            self.elementClassHandler.add(heading, 'bounce');
                            self.elementClassHandler.add(heading, 'infinite');
                        } else {
                            self.elementClassHandler.remove(heading, 'bounce');
                            self.elementClassHandler.remove(heading, 'infinite');
                        }
                    });
                    el.setAttribute(animationTriggerAttr, '1');
                }
            });
            searchDurationInputs.forEach((el) => {
                if (!el.hasAttribute(animationTriggerAttr)) {
                    el.addEventListener('focus', () => {
                        self.elementClassHandler.remove(heading, 'bounce');
                        self.elementClassHandler.remove(heading, 'infinite');
                    });
                    el.setAttribute(animationTriggerAttr, '1');
                }
            });
        }
    }

    setupManualInputListener() {
        const button = document.querySelector(this.selectors.dataAttr(this.addAddressButton) + ' button');
        let manualPropertyInputs = this.selectors.querySelectorArray(this.selectors.dataAttr(this.manualAddressBlock) + ' input'),
            manualDurationInputs = this.selectors.querySelectorArray(this.selectors.dataAttr(this.manualAddressDurationBlock) + ' input'),
            manualAddressInputs = manualPropertyInputs.concat(manualDurationInputs),
            self = this;
        if (button && manualAddressInputs) {
            this.checkDisableAddressButton(button, manualAddressInputs);
            manualAddressInputs.forEach((el) => {
                let disableFindAttr = 'data-check-disabled-find';
                if (!el.hasAttribute(disableFindAttr)) {
                    el.addEventListener('input', () => {
                        self.checkDisableAddressButton(button, manualAddressInputs);
                    });
                    el.setAttribute(disableFindAttr, '1');
                }
            });
        }
    }

    setupAddAddressButtonListener() {
        const element = document.querySelector(this.selectors.dataAttr(this.addAddressButton) + ' button') || null;
        if (element) {
            element.addEventListener('click', (e) => {
                e.preventDefault();
                this.addManualAddress();
            });
        }
    }

    setupUpdateAddressButtonListener() {
        const element = document.querySelector(this.selectors.dataAttr(this.updateAddressButton)) || null;
        if (element) {
            element.addEventListener('click', (e) => {
                let addressIndex = document.querySelector(this.selectors.dataAttr(this.manualAddressIndex)).value;
                e.preventDefault();
                this.postcodeToUpper('#' + this.elementIdentifier + '_manual_form_postcode');
                this.clearAddressDeleteMessage();
                this.requestHandler.post(constants.CHECKOUTPATH + '/' + this.addressType + '/update' +
                (this.addressType === 'billing-address' ? '/' + addressIndex : ''),
                this.formFieldHandler.getFormValues(this.selectors.dataAttr(this.manualForm)),
                this.updateAddressApiCallBack.bind(this)
                );
            });
        }
    }

    setupCancelAddressButtonListener() {
        const element = document.querySelector(this.selectors.dataAttr(this.cancelUpdateAddress)) || null,
            addressIndex = document.querySelector(this.selectors.dataAttr(this.manualAddressIndex)).value;
        if (element) {
            element.addEventListener('click', (e) => {
                e.preventDefault();
                this.clearManualForm();
                this.validateMyAddressList();
                if (addressIndex == 0 && this.multipleAddresses) {
                    this.elementClassHandler.remove(this.container, this.css.editingCurrentAddress);
                }
            });
        }
    }

    setupFormSubmitListener() {
        const form = document.querySelector('[' + this.selectors.dataAttrId + ']').closest('form');
        if (form && !form.dataset.hasSubmitListener) {
            form.addEventListener('submit', (event) => {
                if (this.isManualBillingAddressPending()) {
                    event.preventDefault();
                    form.dataset.queuedForSubmit = true;
                    this.addManualAddress();
                }
            });
            form.dataset.hasSubmitListener = true;
        }
    }

    isManualBillingAddressPending() {
        const button = document.querySelector(this.selectors.dataAttr(this.addAddressButton) + ' button');
        return button.dataset.isPending;
    }

    addManualAddress() {
        this.postcodeToUpper('#' + this.elementIdentifier + '_manual_form_postcode');
        let address = this.formToCollectionId(this.manualForm);
        if (address.houseNumber && address.postcode && !this.checkAddressUnique(address)) {
            return;
        }
        if (this.multipleAddresses && address.years == 0 && address.months == 0) {
            this.showError('Please enter a least one month');
            return;
        }
        this.clearAddressDeleteMessage();
        this.loadingOverlay.taskStarted();
        let formData = this.formFieldHandler.getFormValues(this.selectors.dataAttr(this.manualForm));
        let checkbox = document.querySelector(this.checkbox);
        if (checkbox == null) {
            formData[this.useBillingAddressAsDeliveryAddress] = false;
        } else {
            formData[this.useBillingAddressAsDeliveryAddress] = checkbox.checked === true;
        }
        this.requestHandler.post(
            constants.CHECKOUTPATH + '/' + this.addressType + '/add',
            formData,
            this.addAddressApiCallBack.bind(this)
        );
    }

    setupManualAddressLinkListener() {
        const element = document.querySelector(this.selectors.dataAttr(this.manualAddressLink)) || null;
        if (element) {
            element.addEventListener('click', (e) => {
                e.preventDefault();
                this.activateManualAddressForm();
            });
        }
    }

    activateManualAddressForm() {
        this.clearSearchForm();
        this.clearAddressDeleteMessage();

        if (this.durationFields && this.multipleAddresses) {
            const heading = document.querySelector(this.selectors.dataAttr(this.searchAddressDurationBlock) + ' [data-address-heading]');
            this.elementClassHandler.remove(heading, 'bounce');
            this.elementClassHandler.remove(heading, 'infinite');
        }
        this.elementClassHandler.hide(document.querySelector(this.selectors.dataAttr(this.addressSearchResults)));
        this.showBlocks([this.manualAddressBlock, this.addAddressBlock, this.manualAddressDurationBlock]);
        this.toggleRequiredFields('manual');
        this.setupManualInputListener();
    }

    setupSearchAddressLinkListener() {
        const element = document.querySelector(this.selectors.dataAttr(this.searchAddressLink)) || null;
        if (element) {
            element.addEventListener('click', (e) => {
                e.preventDefault();
                this.clearAddressDeleteMessage();
                this.clearManualForm();
                this.showBlocks([this.searchAddressBlock, this.searchAddressDurationBlock]);
                this.toggleRequiredFields();
            });
        }
    }

    formToCollectionId(element) {
        const container = document.querySelector(this.selectors.dataAttr(element));
        let result = {},
            houseNumberElem = container.querySelector(this.selectors.attrSelector('name', element + '[houseNumberName]'));

        if (!houseNumberElem) {
            houseNumberElem = container.querySelector(this.selectors.attrSelector('name', element + '[houseNumber]'));
        }

        result.houseNumber = houseNumberElem.value;
        result.postcode = container.querySelector(this.selectors.attrSelector('name', element + '[postcode]')).value;

        if (this.multipleAddresses) {
            result.months = document.querySelector(this.selectors.nameAttr(element + '[months][months]')).value;
            result.years = document.querySelector(this.selectors.nameAttr(element + '[months][years]')).value;
        }

        return result;
    }

    myAddressesApiCallBack(response) {
        if (this.responseHandler.isSuccess(response)) {
            const myAddresses = this.responseHandler.getData(response) || [];
            this.clearAddressesFromCollection();
            myAddresses.forEach((item) => {
                if (item['postcode']) {
                    this.addMyAddress(item);
                }
            });
            this.validateMyAddressList();
        } else {
            console.error(this.responseHandler.getErrors(response), 'myAddressesApiCallBack Error');
        }
    }

    constructManualFormDataFromAddressLookup(address) {
        let propertyConstructArray = this.fetchPropertyConstructArray(address);
        let stub = `${this.manualFormPrefix}`,
            data = {
                [stub + '[property]']: this.constructProperty(address, propertyConstructArray),
                [stub + '[houseNumber]']: this.fetchBuildingNumberOrName(address),
                [stub + '[street]']: address['street'] ? address['street'] : '',
                [stub + '[locality]']: '',
                [stub + '[town]']: address['town'] ? address['town'] : '',
                [stub + '[county]']: address['stateRegion'] ? address['stateRegion'] : '',
                [stub + '[postcode]']: address['postcode'] ? address['postcode'] : '',
                [stub + '[_token]']: this.manualFormToken
            };

        if (address.hasOwnProperty('years')) {
            data[stub + '[months][years]'] = address['years'];
            data[stub + '[months][months]'] = address['months'];
        }
        return data;
    }

    constructProperty(address, fieldsToConcatenate) {
        let propertyComponents = [];
        const keys = Object.keys(address);

        keys.forEach((key) => {
            if (fieldsToConcatenate.includes(key)) {
                propertyComponents.push(address[key]);
            }
        });
        return propertyComponents.join(', ');
    }

    fillInputField(propertyValue, propertySelector) {
        let formFillElem = document.querySelector(propertySelector);
        formFillElem.value = propertyValue;
    }

    getAddressSearchApiCallBack(response) {
        let address = this.formToCollectionId(this.searchForm);

        this.clearAddressSearchMessage();

        if (Array.isArray(response)) {
            let filteredAddress = this.filterAddressesByHouseNumberName(response, address.houseNumber);
            if (filteredAddress) {
                this.matchingAddresses = [filteredAddress];
            } else {
                this.matchingAddresses = response;
            }
            this.handleAddressSearchResults();
        } else {
            this.elementClassHandler.hide(document.querySelector(this.selectors.dataAttr(this.addressSearchResults)));
        }

        this.formValidator.setupRemoveErrorEventListeners();
        this.formValidator.forceNumberOnlyFields();
        this.setupManualAddressLinkListener();
        this.setupSearchInputListener();
        this.loadingOverlay.taskFinished();
    }

    fetchAddressApiCallBack(address) {
        if (this.multipleAddresses) {
            address['years'] = document.querySelector(this.selectors.nameAttr(this.searchForm + '[months][years]')).value;
            address['months'] = document.querySelector(this.selectors.nameAttr(this.searchForm + '[months][months]')).value;
        }

        this.requestHandler.post(constants.CHECKOUTPATH + '/' + this.addressType + '/add',
            this.constructManualFormDataFromAddressLookup(address),
            this.addAddressApiFromSearchCallBack.bind(this)
        );
    }

    filterAddressesByHouseNumberName(addresses, houseNumberName) {
        const pattern = new RegExp('^' + houseNumberName + ', |^' + houseNumberName + ' ', 'i');
        for (let address of addresses) {
            if (pattern.test(address.address)) {
                return address;
            }
        }
        return null;
    }

    addAddressApiFromSearchCallBack(response) {
        document.querySelector(this.selectors.dataAttr(this.manualAddressBlock)).innerHTML = this.responseHandler.getFormAddress(response);
        if (this.durationFields) {
            document.querySelector(this.selectors.dataAttr(this.manualAddressDurationBlock)).innerHTML = this.responseHandler.getFormDuration(response);
        }
        if (this.responseHandler.isSuccess(response)) {
            this.addMyAddress(this.getManualAddress());
            this.submitFormIfQueued();
            this.clearSearchForm();
            this.setupSearchAddressLinkListener();
            this.setupUpdateAddressButtonListener();
            this.setupAddAddressButtonListener();
            this.setupCancelAddressButtonListener();
        } else {
            this.activateManualAddressForm();
        }
        this.loadingOverlay.taskFinished();
    }

    addAddressApiCallBack(response) {
        if (this.responseHandler.isSuccess(response)) {
            this.addMyAddress(this.getManualAddress());
            this.submitFormIfQueued();
        }
        document.querySelector(this.selectors.dataAttr(this.manualAddressBlock)).innerHTML = this.responseHandler.getFormAddress(response);
        if (this.durationFields) {
            document.querySelector(this.selectors.dataAttr(this.manualAddressDurationBlock)).innerHTML = this.responseHandler.getFormDuration(response);
        }
        this.formValidator.setupRemoveErrorEventListeners();
        this.formValidator.forceNumberOnlyFields();
        this.setupSearchAddressLinkListener();
        this.setupUpdateAddressButtonListener();
        this.setupAddAddressButtonListener();
        this.setupCancelAddressButtonListener();
        this.setupSearchInputListener();
        this.loadingOverlay.taskFinished();
    }

    submitFormIfQueued() {
        const form = document.querySelector('[' + this.selectors.dataAttrId + ']').closest('form');
        if (form && form.dataset.queuedForSubmit) {
            form.submit();
        }
    }

    updateAddressApiCallBack(response) {
        document.querySelector(this.selectors.dataAttr(this.manualAddressBlock)).innerHTML = this.responseHandler.getFormAddress(response);
        if (this.durationFields) {
            document.querySelector(this.selectors.dataAttr(this.manualAddressDurationBlock)).innerHTML = this.responseHandler.getFormDuration(response);
        }
        if (this.responseHandler.isSuccess(response)) {
            this.updateMyAddress(this.getManualAddress());
        } else {
            this.showBlocks([this.manualAddressBlock, this.updateAddressBlock, this.manualAddressDurationBlock]);
        }
        this.formValidator.setupRemoveErrorEventListeners();
        this.formValidator.forceNumberOnlyFields();
        this.setupSearchAddressLinkListener();
        this.setupUpdateAddressButtonListener();
        this.setupAddAddressButtonListener();
        this.setupCancelAddressButtonListener();
        this.setupSearchInputListener();
    }

    handleAddressSearchResults() {
        const searchResultsContainer = document.querySelector(this.selectors.dataAttr(this.addressSearchResults)),
            select = searchResultsContainer.querySelector('select');
        this.elementClassHandler.hide(searchResultsContainer);
        this.setAddressSearchMessage();
        if (this.matchingAddresses.length === 0) {
            return;
        } else if (this.matchingAddresses.length === 1) {
            const address = this.matchingAddresses[0];

            this.requestHandler.get(
                this.geoPostcodeLookupHostname + '/address/' + address.id,
                null,
                this.fetchAddressApiCallBack.bind(this)
            );
            return;
        }
        select.innerHTML = '';
        let defaultOption = document.createElement('option');
        defaultOption.textContent = 'Select Address';
        select.appendChild(defaultOption);

        this.matchingAddresses.map((address) => {
            let newAddress = new Option(address.address, address.id);
            select.appendChild(newAddress);
        });
        this.removeRequiredFields();
        this.elementClassHandler.hide(document.querySelector(this.selectors.dataAttr(this.searchAddressBlock)));
        if (this.multipleAddresses) {
            this.elementClassHandler.hide(document.querySelector(this.selectors.dataAttr(this.searchAddressDurationBlock)));
        }
        this.elementClassHandler.show(searchResultsContainer);
    }

    updateMyAddress(address) {
        let addressIndex = address.index || null,
            myAddressesMapped = this.myAddresses.map((item, index) => index === parseInt(addressIndex, 10) ? address : item);

        this.clearMyAddresses();
        this.clearAddressesFromCollection();
        myAddressesMapped.forEach(address => this.addMyAddress(address));
        this.validateMyAddressList();
        if (addressIndex == 0 && this.multipleAddresses) {
            this.elementClassHandler.remove(this.container, this.css.editingCurrentAddress);
        }
    }

    clearMyAddresses() {
        const list = document.querySelector(this.selectors.dataAttr(this.myAddressList));
        list.innerHTML = '';
        this.myAddresses = [];
    }

    addMyAddress(address) {
        const list = document.querySelector(this.selectors.dataAttr(this.myAddressList)),
            li = document.createElement('div'),
            addressIndex = this.myAddresses.length;
        this.hideError();
        this.clearSearchForm();
        this.createMyAddressContent(li, address, addressIndex);
        li.setAttribute('data-address-id', `my_${this.elementIdentifier}_${addressIndex}`);
        list.appendChild(li);

        document.querySelector(this.selectors.dataAttr(`edit_my_${this.elementIdentifier}_${addressIndex}`)).addEventListener('click', (e) => {
            const address = this.myAddresses[addressIndex] || {},
                getProperty = propertyName => address.hasOwnProperty(propertyName) ? address[propertyName] : '';

            e.preventDefault();

            this.clearManualForm();
            this.fillInputField(getProperty('property'), this.selectors.nameAttr(this.manualForm + '[property]'));
            this.fillInputField(getProperty('houseNumber'), this.selectors.nameAttr(this.manualForm + '[houseNumber]'));
            this.fillInputField(getProperty('street'), this.selectors.nameAttr(this.manualForm + '[street]'));
            this.fillInputField(getProperty('locality'), this.selectors.nameAttr(this.manualForm + '[locality]'));
            this.fillInputField(getProperty('town'), this.selectors.nameAttr(this.manualForm + '[town]'));
            this.fillInputField(getProperty('county'), this.selectors.nameAttr(this.manualForm + '[county]'));
            this.fillInputField(getProperty('postcode'), this.selectors.nameAttr(this.manualForm + '[postcode]'));

            if (document.querySelector(this.selectors.dataAttr(this.manualAddressIndex))) {
                document.querySelector(this.selectors.dataAttr(this.manualAddressIndex)).value = addressIndex;
            }

            if (this.multipleAddresses) {
                this.fillInputField(getProperty('years'), this.selectors.nameAttr(this.manualForm + '[months][years]'));
                this.fillInputField(getProperty('months'), this.selectors.nameAttr(this.manualForm + '[months][months]'));
            }

            this.showBlocks([this.manualAddressBlock, this.updateAddressBlock, this.manualAddressDurationBlock]);

            if (this.multipleAddresses) {
                if (addressIndex == 0) {
                    this.elementClassHandler.add(this.container, this.css.editingCurrentAddress);
                } else {
                    this.elementClassHandler.remove(this.container, this.css.editingCurrentAddress);
                }
                this.formValidator.forceNumberOnlyFields();
            }
            this.formValidator.setupRemoveErrorEventListeners();
            this.toggleRequiredFields('manual');
            this.setupManualInputListener();
        });

        document.querySelector(this.selectors.dataAttr(`remove_my_${this.elementIdentifier}_${addressIndex}`)).addEventListener('click', (e) => {
            let handleDelete, handleClose;

            e.preventDefault();

            handleDelete = () => {
                this.loadingOverlay.taskStarted();
                this.requestHandler.post(constants.CHECKOUTPATH + '/' + this.addressType + '/delete' +
                (this.multipleAddresses ? '/' + addressIndex : ''),
                {},
                this.deleteAddressesApiCallBack.bind(this)
                );
                document.querySelector(this.selectors.modalDataAttr(`${this.deleteModal}_close`)).removeEventListener('click', handleClose);
                document.querySelector(this.selectors.modalDataAttr(`${this.deleteModal}_submit`)).removeEventListener('click', handleDelete);
                if (addressIndex == 0 && this.multipleAddresses) {
                    this.elementClassHandler.remove(this.container, this.css.currentAddressAdded);
                }
            };

            handleClose = () => {
                document.querySelector(this.selectors.modalDataAttr(`${this.deleteModal}_close`)).removeEventListener('click', handleClose);
                document.querySelector(this.selectors.modalDataAttr(`${this.deleteModal}_submit`)).removeEventListener('click', handleDelete);
            };

            document.querySelector(this.selectors.modalDataAttr(`${this.deleteModal}_submit`)).addEventListener('click', handleDelete);
            document.querySelector(this.selectors.modalDataAttr(`${this.deleteModal}_close`)).addEventListener('click', handleClose);
        });

        this.addAddressToCollection(address);

        this.myAddresses.push(address);
        this.elementClassHandler.show(list);
        this.validateMyAddressList();

        if (this.multipleAddresses) {
            this.elementClassHandler.add(this.container, this.css.currentAddressAdded);
        }

    }

    addAddressToCollection(address) {
        if (this.multipleAddresses) {
            const
                collectionBlock = document.querySelector(this.selectors.dataAttr(this.collectionBlock)) || null,
                prototype = collectionBlock.getAttribute('data-prototype') || null;

            if (prototype) {
                const element = document.createElement('div'),
                    baseField = 'form[billingAddressCollection][billingAddresses][' + this.myAddresses.length + ']',
                    getProperty = propertyName => address.hasOwnProperty(propertyName) ? address[propertyName] : '';

                element.innerHTML = prototype.replace(/__name__/g, this.myAddresses.length);
                collectionBlock.appendChild(element);

                this.fillInputField(getProperty('property'), this.selectors.nameAttr(baseField + '[property]'));
                this.fillInputField(getProperty('houseNumber'), this.selectors.nameAttr(baseField + '[houseNumber]'));
                this.fillInputField(getProperty('street'), this.selectors.nameAttr(baseField + '[street]'));
                this.fillInputField(getProperty('locality'), this.selectors.nameAttr(baseField + '[locality]'));
                this.fillInputField(getProperty('town'), this.selectors.nameAttr(baseField + '[town]'));
                this.fillInputField(getProperty('county'), this.selectors.nameAttr(baseField + '[county]'));
                this.fillInputField(getProperty('postcode'), this.selectors.nameAttr(baseField + '[postcode]'));
                this.fillInputField(getProperty('years'), this.selectors.nameAttr(baseField + '[months][years]'));
                this.fillInputField(getProperty('months'), this.selectors.nameAttr(baseField + '[months][months]'));
            }
        }
    }

    clearAddressesFromCollection() {
        const collectionBlock = document.querySelector(this.selectors.dataAttr(this.collectionBlock)) || null;

        if (this.multipleAddresses && collectionBlock) {
            collectionBlock.innerHTML = '';
        }
    }

    deleteAddressesApiCallBack(response) {
        if (this.responseHandler.isSuccess(response)) {
            const data = this.responseHandler.getData(response) || [],
                addressIndex = data['addressIndex'] || 0;

            let myAddressesFiltered = this.myAddresses.filter((item, index) => index !== parseInt(addressIndex, 10));

            this.clearMyAddresses();
            this.clearAddressesFromCollection();
            myAddressesFiltered.forEach((address) => {
                this.addMyAddress(address);
            });

            this.validateMyAddressList();
            this.setupSearchInputListener();
            this.toggleRequiredFields();
        } else {
            const element = document.querySelector(this.selectors.dataAttr(this.errorMessage));
            this.elementClassHandler.show(element);
            element.innerHTML = 'Could not delete address details';
        }
        this.loadingOverlay.taskFinished();
    }

    createMyAddressContent(element, address, addressIndex) {
        let div, ul, addressHeading = (window.location.href.includes('carphonewarehouse') && this.elementIdentifier == 'billing_address') ? 'Billing Address' : 'Delivery Address';

        if (this.multipleAddresses) {
            addressHeading = addressIndex === 0 ? 'Current Address' : 'Previous Address';
        }

        this.appendNode(element, 'h3', addressHeading + `<a data-address-id="edit_my_${this.elementIdentifier}_${addressIndex}"
            href="#" class="edit-address">Edit</a> `, 'address-heading'
        );

        div = this.appendNode(element, 'div', '', 'address-box');
        ul = this.appendNode(div, 'div', '', 'address-lines');

        div.setAttribute(this.collectionIdentifier, this.formatCollectionId(address));

        Object.keys(address).forEach((key) => {
            if (address[key]) {
                let houseNumber = address['houseNumber'];
                switch (key) {
                    case 'street':
                        this.appendNode(ul, 'div', (houseNumber ? houseNumber + '&nbsp;' : '') + address[key], key);
                        break;
                    case 'property':
                    case 'locality':
                    case 'town':
                    case 'county':
                    case 'postcode':
                        this.appendNode(ul, 'div', address[key], key);
                        break;
                    case 'months':
                        if (this.multipleAddresses) {
                            this.appendNode(ul, 'div', `<span>Time at this address</span>
                                                      ${address['years']} Years ${address['months']} Months`, 'duration');
                        }
                        break;
                }
            }
        });

        this.appendNode(div, 'span',
            `<a data-address-id="${`remove_my_${this.elementIdentifier}_${addressIndex}`}" data-toggle="modal"
                data-target='${this.selectors.modalDataAttr(this.deleteModal)}' href="#" class="remove-icon" alt="delete address"></a>`, 'remove-address'
        );
    }

    appendNode(element, type, content = '', className = '') {
        const node = document.createElement(type);
        if (content) {
            node.innerHTML = content;
        }
        if (className) {
            this.elementClassHandler.add(node, className);
        }
        element.appendChild(node);

        return node;
    }

    getManualAddress() {
        const index = document.querySelector(this.selectors.dataAttr(this.manualAddressIndex)) || null,
            years = document.querySelector(this.selectors.nameAttr(this.manualForm + '[months][years]')) || null,
            months = document.querySelector(this.selectors.nameAttr(this.manualForm + '[months][months]')) || null;

        return {
            'property': document.querySelector(this.selectors.nameAttr(this.manualForm + '[property]')).value,
            'houseNumber': document.querySelector(this.selectors.nameAttr(this.manualForm + '[houseNumber]')).value,
            'street': document.querySelector(this.selectors.nameAttr(this.manualForm + '[street]')).value,
            'locality': document.querySelector(this.selectors.nameAttr(this.manualForm + '[locality]')).value,
            'town': document.querySelector(this.selectors.nameAttr(this.manualForm + '[town]')).value,
            'county': document.querySelector(this.selectors.nameAttr(this.manualForm + '[county]')).value,
            'postcode': document.querySelector(this.selectors.nameAttr(this.manualForm + '[postcode]')).value,
            'index': index ? index.value : null,
            'years': years ? years.value : null,
            'months': months ? months.value : null
        };
    }

    showBlocks(blocks) {
        this.startLoad();
        this.elementClassHandler.hideElements(this.selectors.querySelectorArray(this.selectors.dataAttr(this.searchAddressBlock)));
        this.elementClassHandler.hideElements(this.selectors.querySelectorArray(this.selectors.dataAttr(this.manualAddressBlock)));
        if (this.durationFields) {
            this.elementClassHandler.hideElements(this.selectors.querySelectorArray(this.selectors.dataAttr(this.searchAddressDurationBlock)));
            this.elementClassHandler.hideElements(this.selectors.querySelectorArray(this.selectors.dataAttr(this.manualAddressDurationBlock)));
        }
        this.elementClassHandler.hide(document.querySelector(this.selectors.dataAttr(this.updateAddressBlock)));
        this.elementClassHandler.hide(document.querySelector(this.selectors.dataAttr(this.addAddressBlock)));
        blocks.forEach((block) => {
            if (block === this.searchAddressBlock || block === this.manualAddressBlock) {
                this.elementClassHandler.showElements(this.selectors.querySelectorArray(this.selectors.dataAttr(block)));
            } else if (this.durationFields || (block !== this.searchAddressDurationBlock && block !== this.manualAddressDurationBlock)) {
                this.elementClassHandler.show(document.querySelector(this.selectors.dataAttr(block)));
            }
        });
        this.endLoad();
    }

    validateMyAddressList() {
        if (this.multipleAddresses) {
            let totalMonths = 0;
            this.myAddresses.forEach((address) => {
                totalMonths += (parseInt(address.years, 10) ? parseInt(address.years, 10) * 12 : 0) +
                    parseInt(address.months, 10);
            });
            if (totalMonths >= this.requiredHistory) {
                this.showBlocks([]);
                this.elementClassHandler.hide(document.querySelector(this.selectors.dataAttr(this.myAddressList + '_notice')));
                this.elementClassHandler.show(document.querySelector(this.selectors.dataAttr(this.myAddressList + '_success')));
                this.removeRequiredFields();
            } else {
                this.showBlocks([this.searchAddressBlock, this.searchAddressDurationBlock]);
                if (totalMonths > 0) {
                    this.elementClassHandler.show(document.querySelector(this.selectors.dataAttr(this.myAddressList + '_notice')));
                }
                this.elementClassHandler.hide(document.querySelector(this.selectors.dataAttr(this.myAddressList + '_success')));
                this.clearSearchForm();
                this.clearManualForm();
                this.toggleRequiredFields();
            }
        } else if (this.myAddresses.length > 0) {
            this.showBlocks([]);
            this.removeRequiredFields();
        } else {
            this.showBlocks([this.searchAddressBlock, this.searchAddressDurationBlock]);
        }
        this.checkForNonStandardPostcode();
    }

    checkDisableAddressButton(button, addressInputs) {
        let self = this,
            disabled = 0;
        addressInputs.forEach((el) => {
            if (disabled == 1) {
                return;
            }
            if (!el.validity.valid || (!el.value && el.hasAttribute('required'))) {
                button.dataset.isPending = false;
                self.elementClassHandler.remove(button, 'pulse');
                button.setAttribute('disabled', 'disabled');
                disabled = 1;
            }
        });
        if (disabled !== 1) {
            button.dataset.isPending = true;
            button.removeAttribute('disabled');
            self.elementClassHandler.add(button, 'pulse');
        }
    }

    clearFields(fields, formElement) {
        fields.forEach((fieldName) => {
            let field = document.querySelector(this.selectors.nameAttr(formElement + fieldName));
            if (field) {
                field.value = '';
                this.elementClassHandler.remove(field.parentNode, 'is-dirty');
                this.elementClassHandler.remove(field.parentNode, 'is-valid');
                this.elementClassHandler.remove(field.parentNode, 'is-invalid');
            }
        });
    }

    clearManualForm() {
        this.clearFields(['[property]', '[houseNumber]', '[street]', '[locality]', '[town]', '[county]', '[postcode]',
            '[months][years]', '[months][months]'], this.manualForm);

        if (document.querySelector(this.selectors.dataAttr(this.manualAddressIndex))) {
            document.querySelector(this.selectors.dataAttr(this.manualAddressIndex)).value = '';
        }
        this.clearAddressDeleteMessage();
    }

    clearSearchForm() {
        this.clearAddressSearchMessage();
        this.clearFields(['[houseNumberName]', '[postcode]', '[months][years]', '[months][months]'], this.searchForm);
        this.setupSearchInputListener();
    }

    setAddressSearchMessage() {
        const element = document.querySelector(this.selectors.dataAttr(this.addressSearchMessage));
        if (!this.matchingAddresses.length) {
            element.innerHTML = 'Sorry, no address was found matching your search criteria.';
            this.elementClassHandler.alertError(element);
            this.elementClassHandler.show(element);
        } else if (this.matchingAddresses.length > 1) {
            if (this.multipleAddresses) {
                let addressIndex = this.myAddresses.length;
                element.innerHTML = addressIndex == 0 ? 'Please select your address from the list.' : 'Please select your previous address from the list.';
            } else {
                element.innerHTML = 'Please select an address from the list.';
            }
            this.elementClassHandler.removeAlerts(element);
            this.elementClassHandler.show(element);
        }
    }

    clearAddressSearchMessage() {
        const element = document.querySelector(this.selectors.dataAttr(this.addressSearchMessage));
        this.elementClassHandler.hide(element);
        element.innerHTML = '';
    }

    clearAddressDeleteMessage() {
        const element = document.querySelector(this.selectors.dataAttr(this.errorMessage));
        this.elementClassHandler.hide(element);
        element.innerHTML = '';
    }

    checkForNonStandardPostcode() {
        if (this.myAddresses.length) {
            this.checkPostcode(this.myAddresses[0].postcode);
        } else {
            this.actionPostcode(false);
        }
    }

    checkPostcode(postcode) {
        this.loadingOverlay.taskStarted();
        this.requestHandler.post(
            constants.CHECKOUTPATH + '/delivery-address/isnonstandard',
            {
                'postcode': postcode
            },
            this.checkPostcodeCallBack.bind(this)
        );
    }

    checkPostcodeCallBack(response) {
        this.actionPostcode(response.result);
        this.loadingOverlay.taskFinished();
    }

    actionPostcode(nonstandard) {
        if (nonstandard) {
            this.showElementIfExists(this.nonStandardPostcodeMessage);
            this.hideElementIfExists(this.slotPicker);
        } else {
            this.hideElementIfExists(this.nonStandardPostcodeMessage);
            this.showElementIfExists(this.slotPicker);
        }
    }

    hideElementIfExists(selector) {
        const element = document.querySelector(this.selectors.dataAttr(selector));
        if (element) {
            this.elementClassHandler.hide(element);
        }
    }

    showElementIfExists(selector) {
        const element = document.querySelector(this.selectors.dataAttr(selector));
        if (element) {
            this.elementClassHandler.show(element);
        }
    }

    readConfig() {
        const configString = this.container.getAttribute(this.configAttribute);
        if (!configString) {
            return;
        }
        let config = JSON.parse(configString);
        if (!config) {
            return;
        }
        if (config.hasOwnProperty('requiredHistory')) {
            this.requiredHistory = config.requiredHistory;
        }
        if (config.hasOwnProperty('multipleAddresses')) {
            this.multipleAddresses = config.multipleAddresses;
        }
        if (config.hasOwnProperty('customAddressHeading')) {
            this.addressHeading = config.customAddressHeading;
        }
    }

    fetchPropertyConstructArray(address) {
        let buildingName = ['organisation', 'subBuilding'];
        if (address['buildingNumber'] && address['buildingNumber'] !== '') {
            buildingName = ['organisation', 'subBuilding', 'buildingName'];
        }
        return buildingName;
    }

    fetchBuildingNumberOrName(address) {
        return address['buildingNumber'] ? address['buildingNumber'] : address['buildingName'];
    }
    
    setupPostCode() {
        $('#billing_address_search_form_postcode, #billing_address_manual_form_postcode, #delivery_address_search_form_postcode, #delivery_address_manual_form_postcode').bind('input propertychange', function(e) {
            let pattern = /^(JE|GY)/;
            let postcode = $(this);
            if (postcode.val().length >= 2 && pattern.test(postcode.val().toUpperCase())) {
                postcode.val('');
                if (!$('span').hasClass('errorMessagePostCode')) {
                    postcode.after('<span class="errorMessagePostCode" style="box-sizing: border-box;width: 100%;color: #d0021b;font-size: 14px;">We do not deliver to JE or GY postcodes</span>');
                }
                setTimeout(function() {
                    $('.errorMessagePostCode').fadeTo(0, 2000);
                    $('.errorMessagePostCode').remove();
                }, 3000);
                e.preventDefault();
            } else {
                let searchStoresEl = document.querySelector('[data-stores-search]');
                if (searchStoresEl && $(this).val().length >= 3) {
                    const el = document.getElementById('search-stores-input');
                    el.dispatchEvent(new Event('input', $('#search-stores-input').val($(this).val())));
                }
            }
        });
    }
}
//Keyboard focus is moved to the Select Address 
const selectAddress = document.querySelector('.address-results-select');
if (selectAddress) {
    const observer = new MutationObserver(() => {
        if (selectAddress.options.length > 1) {
            selectAddress.focus();
        }
    });
    observer.observe(selectAddress, {
        childList: true
    });
}
